<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiArrowRight } from "@mdi/js"
  import type {
    GuidedProgressMetric,
    GuidedProgressStep,
  } from "../../../../../../../_shared/_logic/guided-progress-contract"

  let {
    label,
    actionLabel,
    headline,
    description,
    currentItemLabel = null,
    metrics = [],
    steps = [],
    progressLabel,
    loading = false,
    actionDisabled = false,
    onOpen,
  }: {
    label: string
    actionLabel: string
    headline: string
    description: string
    currentItemLabel?: string | null
    metrics?: GuidedProgressMetric[]
    steps?: GuidedProgressStep[]
    progressLabel: string
    loading?: boolean
    actionDisabled?: boolean
    onOpen: () => void
  } = $props()
</script>

<ActionBox {label} darkMode={true}>
  {#snippet buttonSnippet()}
    <CTA
      type="primaryButton"
      small={true}
      content={actionLabel}
      darkMode={false}
      disabled={loading || actionDisabled}
      onClick={onOpen}
    >
      {#snippet iconSnippet()}
        <Icon path={mdiArrowRight} />
      {/snippet}
    </CTA>
  {/snippet}

  <div class="guided-card" class:guided-card--loading={loading} aria-busy={loading}>
    <div class="guided-copy">
      <strong>{headline}</strong>
      <span>{description}</span>
      {#if currentItemLabel}
        <p>{currentItemLabel}</p>
      {/if}
    </div>

    {#if metrics.length}
      <div class="metric-row" aria-label={progressLabel}>
        {#each metrics as metric (metric.label)}
          <div>
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
          </div>
        {/each}
      </div>
    {/if}

    {#if steps.length}
      <ol class="step-dots" aria-label={progressLabel}>
        {#each steps as step, index (step.id)}
          <li class="dot dot--{step.state}" title={step.title}>
            <span>{index + 1}</span>
            <em>{step.title}</em>
          </li>
        {/each}
      </ol>
    {:else if loading}
      <div class="loading-lines" aria-label="Wird geladen">
        <span></span>
        <span></span>
        <span></span>
      </div>
    {/if}
  </div>
</ActionBox>

<style lang="less">
  .guided-card {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .guided-copy {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .guided-copy strong {
    color: var(--text-invers-100);
    font-size: 1rem;
    line-height: 1.2;
  }

  .guided-copy span,
  .guided-copy p {
    color: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
    line-height: 1.35;
    margin: 0;
  }

  .metric-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .metric-row div {
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 18%, transparent);
    padding: 0.5rem 0.6rem;
  }

  .metric-row strong,
  .metric-row span {
    display: block;
  }

  .metric-row strong {
    color: var(--text-invers-100);
    font-size: 1.1rem;
    line-height: 1.1;
  }

  .metric-row span {
    color: color-mix(in srgb, var(--text-invers-100) 68%, transparent);
    font-size: 0.78rem;
    line-height: 1.2;
  }

  .step-dots {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 0.35rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .dot {
    min-width: 0;
    border-top: 3px solid color-mix(in srgb, var(--text-invers-100) 18%, transparent);
    padding-top: 0.35rem;
  }

  .dot span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 24%, transparent);
    color: var(--text-invers-100);
    font-size: 0.72rem;
    font-weight: 700;
  }

  .dot em {
    display: block;
    color: color-mix(in srgb, var(--text-invers-100) 66%, transparent);
    font-size: 0.72rem;
    font-style: normal;
    line-height: 1.15;
    margin-top: 0.3rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dot--completed {
    border-color: var(--confirm-100);
  }

  .dot--completed span {
    background: var(--confirm-100);
    border-color: var(--confirm-100);
    color: var(--bg-100);
  }

  .dot--skipped {
    border-color: var(--yellow-100);
  }

  .dot--skipped span {
    border-color: var(--yellow-100);
  }

  .dot--placeholder {
    border-style: dotted;
  }

  .loading-lines {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .loading-lines span {
    min-height: 2.7rem;
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 14%, transparent);
    background: color-mix(in srgb, var(--text-invers-100) 7%, transparent);
    animation: loading-pulse 1.4s ease-in-out infinite alternate;
  }

  @keyframes loading-pulse {
    from {
      opacity: 0.45;
    }
    to {
      opacity: 0.9;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .loading-lines span {
      animation: none;
    }
  }

  @media (max-width: 720px) {
    .metric-row,
    .step-dots {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
</style>
